<template>
  <div class="mt-10">
    <div class="elevation-2 px-5 mt-3">
      <v-row>
        <v-col cols="12">
          <SimplePageHeader title="Monthly View" class="px-4 pr-5 mb-5" />
        </v-col>
      </v-row>
      <v-form ref="filterForm">
        <v-row
          align="center"
          justify="start"
          class="px-5"
        >
          <v-col cols="12" sm="5" md="3" lg="2">
            <MonthPicker
              label="Month"
              :year="monthYear"
              :month="monthMonth"
              @yearChange="(newValue) => this.monthYear = newValue"
              @monthChange="(newValue) => this.monthMonth = newValue"
            ></MonthPicker>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3" xl="2">
            <BusinessEntitiesDropDownList
              v-model="monthBusinessEntityId"
              :clearable="true"
              @businessEntityIdChange="handleBusinessEntityChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3" xl="2">
            <ClientsDropDownList
              :id="monthClientId"
              :businessEntityId="monthBusinessEntityId"
              :clearable="true"
              :isRequired="true"
              @clientIdChange="handleClientIdChange"
            ></ClientsDropDownList>
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3" xl="2">
            <ClientConsultantsDropDownList
              :clientId="monthClientId"
              :assignments="clientAssignments"
              :value="monthUserId"
              clearable
              :isRequired="true"
              :disabled="!monthClientId"
              @consultantIdChange="handleUserIdChange"
            />
          </v-col>
          <v-col cols="12" sm="6" md="3" lg="3" xl="2">
            <v-autocomplete
              v-model="monthAssignmentId"
              :items="filteredAssignments"
              item-text="name"
              item-value="id"
              label="Job Title"
              :rules="[Required('Job Title')]"
              outlined
              clearable
              :disabled="!monthClientId || !monthUserId"
            />
          </v-col>
        </v-row>
        <v-row justify="end" class="px-5 pb-2">
          <v-col cols="12" sm="6">
            <FilterButtons
              @search="handleSearchClick"
              @clear="handleClearClick"
            ></FilterButtons>
          </v-col>
        </v-row>
      </v-form>
    </div>
    <FilterButtonsSmall
      v-if="!showSaveChangesButton"
      @search="handleSearchClick"
      @clear="handleClearClick"
    ></FilterButtonsSmall>
    <div v-if="searched" class="mt-4">
      <v-alert v-if="legacyOverride" type="info" dense text class="mb-4">
        This period has a <strong>legacy edited total</strong> of
        {{ legacyOverride.totalPay | currency }}{{ legacyOverride.details ? ` ("${legacyOverride.details}")` : '' }}.
        Typed hours below are stored, but the payroll and invoicing reports will keep showing the
        edited total for this period until the override is retired.
      </v-alert>

      <v-chip v-if="overtimeEligible && !clientOfRecord" small color="indigo" outlined class="mb-4">
        Overtime eligible - calculated automatically
      </v-chip>

      <template v-if="weeksData.length">
        <v-card v-for="week in weeksData" :key="week.weekKey" class="mb-4">
          <v-card-title class="py-2 subtitle-1 grey lighten-4">
            Week ending {{ formatCellDate(week.weekEnd) }}
            <v-spacer />
            <span class="body-2 grey--text mr-4">{{ week.workedHours | formatDecimal }} hrs</span>
            <span v-if="week.timeOffHours > 0" class="body-2 grey--text mr-4">Time Off: {{ week.timeOffHours | formatDecimal }} hrs</span>
            <FormattedNumberField
              prepend-inner-icon="attach_money"
              :value="week.contractorCommission"
              :disabled="!assignmentData || !assignmentData.contractorCommissionEnabled"
              :required="false"
              :dense="true"
              :validateOnBlur="true"
              :hideDetails="true"
              style="max-width: 160px"
              @valueChange="(val) => contractorCommissionChangeHandler(week, val)"
            />
          </v-card-title>
          <v-card-text class="pt-3">
            <v-simple-table dense class="monthly-typed-table">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="monthly-ts-type">Type</th>
                    <th
                      v-for="(day, i) in week.days"
                      :key="i"
                      class="text-center monthly-ts-header"
                    >
                      <div>{{ dayNames[i] }}</div>
                      <div v-if="day" class="caption grey--text">{{ formatCellDate(day.date) }}</div>
                    </th>
                    <th class="text-right monthly-ts-total">Total</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="!week.typeRows.length">
                    <td :colspan="week.days.length + 2" class="text-center grey--text py-4">
                      Nothing entered for this week.
                    </td>
                  </tr>
                  <tr v-for="typeRow in week.typeRows" :key="typeRow.timeTypeId">
                    <td class="monthly-ts-type">
                      <strong>{{ typeRow.code }}</strong>
                      <div v-if="typeRow.payRate != null" class="caption grey--text">
                        {{ typeRow.payRate | currency }}/hr
                      </div>
                    </td>
                    <td
                      v-for="(day, i) in week.days"
                      :key="i"
                      class="monthly-ts-cell"
                    >
                      <FormattedNumberField
                        v-if="day"
                        :value="typeRow.cells[i] ? typeRow.cells[i].hours : null"
                        :required="false"
                        :dense="true"
                        :validateOnBlur="true"
                        :hideDetails="true"
                        @valueChange="(val) => handleHoursChange(day, typeRow, val)"
                      />
                    </td>
                    <td class="text-right monthly-ts-total">{{ typeRow.hours | formatDecimal }} hrs</td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>

            <div class="mt-3" v-if="availableTypesForWeek(week).length">
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-btn small text color="primary" v-on="on">
                    <v-icon small left>add</v-icon>Add type
                  </v-btn>
                </template>
                <v-list dense>
                  <v-list-item
                    v-for="type in availableTypesForWeek(week)"
                    :key="type.timeTypeId"
                    @click="addTypeRowToWeek(week, type.timeTypeId)"
                  >
                    <v-list-item-title>{{ type.code }}</v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
              <span class="caption grey--text ml-2">
                Only types with a rate configured on this assignment can be added. Holiday, Sick and PTO need no rate and are offered on W2 + Benefits assignments only.
              </span>
            </div>
          </v-card-text>
        </v-card>
      </template>

      <NoDataAvailableMessage v-else :message="noDataMessage" />
    </div>
    <v-row v-else class="mt-5">
      <NoDataAvailableMessage message="Please fill out the filters and then click search"/>
    </v-row>
    <ButtonsSubmitForm
      v-if="showSaveChangesButton"
      :isFormValid="true"
      acceptLabel="Save Changes"
      cancelLabel="Discard"
      @save="saveChanges"
      @cancel="discardChanges"
    ></ButtonsSubmitForm>
    <div
      v-if="showSaveChangesButton"
      class="hide-on-md hide-on-print mt-5 abs-left-0"
    >
      <v-container fluid class="fixed-buttons mt-0 px-0">
        <v-layout row>
          <v-flex xs6 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              color="primary"
              @click="saveChanges"
            >
              Save Changes
            </v-btn>
          </v-flex>
          <v-flex xs6 md2 class="px-5 py-3">
            <v-btn
              block
              depressed
              outlined
              @click="discardChanges"
            >
              Discard
            </v-btn>
          </v-flex>
        </v-layout>
      </v-container>
    </div>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { FilterButtons, FilterButtonsSmall, ButtonsSubmitForm } from '@/components/buttons'
import { MonthPicker } from '@/components/shared/datePickers'
import { BusinessEntitiesDropDownList } from '@/components/businessEntities'
import { ClientsDropDownList, ClientConsultantsDropDownList } from '@/components/clients'
import { FormattedNumberField } from '@/components/shared/textFields'
import Required from '@/lib/formValidations/required'
import { formatDate } from '@/lib/display'
import { SimplePageHeader } from '@/components/pageComponents'
import { NoDataAvailableMessage } from '@/components/reports'
import { buildTypedWeeksData, offerableTypes, findCommissionForWeek, weekHourTotals } from './monthlyTimeManagementHelpers'
import { setOrDeleteQueryParam, hasQueryChanged } from '@/lib/pagingAndSorting'

export default {
  name: 'MonthlyTimeManagement',
  components: {
    FilterButtons,
    FilterButtonsSmall,
    ButtonsSubmitForm,
    MonthPicker,
    BusinessEntitiesDropDownList,
    ClientConsultantsDropDownList,
    ClientsDropDownList,
    FormattedNumberField,
    SimplePageHeader,
    NoDataAvailableMessage
  },
  data () {
    return {
      monthBusinessEntityId: null,
      monthClientId: null,
      monthUserId: null,
      monthAssignmentId: null,
      monthYear: null,
      monthMonth: null,
      clientAssignments: [],
      filteredAssignments: [],
      assignmentData: null,
      timeSheetEntriesToSave: [],
      contractorCommissionsToSave: [],
      manuallyAddedTypeRows: [],
      legacyOverride: null,
      searched: false
    }
  },
  computed: {
    ...mapState('assignment', ['timeTypeRates', 'overtimeEligible', 'payRate', 'employeeTypeId']),
    ...mapState('timeTypes', ['timeTypesList']),
    ...mapState('client', ['clientOfRecord']),
    beginningOfWeekValue () {
      const val = this.$store.state.settings.beginningOfWeek
      return val != null ? val : 0 // default to Sunday (0) matching server default
    },
    dayNames () {
      const allNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
      return Array.from({ length: 7 }, (_, i) => allNames[(this.beginningOfWeekValue + i) % 7])
    },
    offerableTypesList () {
      return offerableTypes({
        timeTypeRates: this.timeTypeRates,
        standardPayRate: this.payRate,
        overtimeEligible: this.overtimeEligible,
        clientOfRecord: this.clientOfRecord,
        employeeTypeId: this.employeeTypeId,
        timeTypesList: this.timeTypesList
      })
    },
    weeksData () {
      const weeks = buildTypedWeeksData({
        assignmentData: this.assignmentData,
        beginningOfWeek: this.beginningOfWeekValue,
        timeTypeRates: this.timeTypeRates,
        standardPayRate: this.payRate
      })

      return weeks.map(week => {
        const existingIds = new Set(week.typeRows.map(row => row.timeTypeId))
        const pendingRows = this.manuallyAddedTypeRows
          .filter(pending => pending.weekKey === week.weekKey && !existingIds.has(pending.timeTypeId))
          .map(pending => {
            const type = this.offerableTypesList.find(t => t.timeTypeId === pending.timeTypeId)
            return {
              timeTypeId: pending.timeTypeId,
              code: type ? type.code : String(pending.timeTypeId),
              payRate: type ? type.payRate : null,
              cells: new Array(week.days.length).fill(null),
              hours: 0
            }
          })

        const typeRows = pendingRows.length
          ? [...week.typeRows, ...pendingRows].sort((a, b) => a.timeTypeId - b.timeTypeId)
          : week.typeRows

        return {
          ...week,
          typeRows,
          ...weekHourTotals(typeRows, this.timeTypesList)
        }
      })
    },
    showSaveChangesButton () {
      return (this.timeSheetEntriesToSave && this.timeSheetEntriesToSave.length > 0) ||
        (this.contractorCommissionsToSave && this.contractorCommissionsToSave.length > 0)
    },
    noDataMessage () {
      return this.clientOfRecord
        ? 'Nothing has been entered for this period. This client is client of record, so no consultant will enter it.'
        : 'Nothing has been entered for this period.'
    }
  },
  created () {
    this.getSettings()
    this.getTimeTypes()
    this.hydrateFromQuery()
  },
  methods: {
    Required,
    formatDate,
    toLocalISODate (date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    ...mapActions('settings', [
      'getSettings'
    ]),
    ...mapActions('timeTypes', [
      'getTimeTypes'
    ]),
    ...mapActions('assignments', [
      'getAssignments'
    ]),
    ...mapActions('assignment', [
      'getAssignment'
    ]),
    ...mapActions('client', [
      'getClient'
    ]),
    ...mapActions('consultantsEditedTotalPay', [
      'getConsultantsEditedTotalPaySilently'
    ]),
    ...mapActions('timeManagement', [
      'getMonthlyTimeSheetEntries',
      'saveTimeSheetEntries',
      'saveContractorCommissions'
    ]),
    async hydrateFromQuery () {
      const { businessEntityId, client, consultant, assignment, month } = this.$route.query
      this.monthBusinessEntityId = Number(businessEntityId) || null
      if (!client || !assignment || !month) return

      const [year, monthNumber] = month.split('-').map(Number)
      this.monthYear = year
      this.monthMonth = monthNumber

      await this.handleClientIdChange(Number(client))

      // An assignment belongs to exactly one consultant, so consultant is derivable when the
      // URL only has client, assignment, and month.
      const targetAssignment = this.clientAssignments.find(a => a.id === Number(assignment))
      const consultantId = consultant ? Number(consultant) : (targetAssignment ? targetAssignment.userId : null)
      if (consultantId) {
        this.handleUserIdChange(consultantId)
      }
      this.monthAssignmentId = Number(assignment)

      await this.$nextTick()
      return this.handleSearchClick()
    },
    handleBusinessEntityChange () {
      this.handleClientIdChange(null)
    },
    handleClientIdChange (newValue) {
      this.monthClientId = newValue
      this.monthUserId = null
      this.monthAssignmentId = null
      this.filteredAssignments = []

      if (!newValue) {
        this.clientAssignments = []
        return Promise.resolve()
      }

      const requestedClientId = newValue
      return this.getAssignments({ clientId: requestedClientId }).then((assignments) => {
        if (this.monthClientId !== requestedClientId) {
          return
        }

        this.clientAssignments = assignments
        const consultantIds = [...new Set(assignments.map(assignment => assignment.userId))]
        if (consultantIds.length === 1) {
          this.handleUserIdChange(consultantIds[0])
        }
      })
    },
    handleUserIdChange (newValue) {
      this.monthUserId = newValue
      if (!newValue) {
        this.monthAssignmentId = null
        this.filteredAssignments = []
        return
      }
      this.filteredAssignments = this.clientAssignments
        .filter((a) => a.userId === newValue)
        .map((a) => ({ id: a.id, name: a.jobTitle + ' - ' + formatDate(a.startAt) }))
      if (this.filteredAssignments.length === 1) {
        this.monthAssignmentId = this.filteredAssignments[0].id
      }
    },
    handleSearchClick () {
      if (this.$refs.filterForm.validate()) {
        this.syncFiltersToQuery()
        return Promise.all([
          this.getMonthlyTimeSheetEntries({
            assignmentId: this.monthAssignmentId,
            month: this.monthMonth,
            year: this.monthYear
          }),
          this.getAssignment({ id: this.monthAssignmentId }),
          this.monthClientId ? this.getClient({ id: this.monthClientId }) : Promise.resolve(null)
        ]).then(([response]) => {
          this.assignmentData = response && response.assignmentsTimeSheetEntries && response.assignmentsTimeSheetEntries.length
            ? response.assignmentsTimeSheetEntries[0]
            : null
          this.timeSheetEntriesToSave = []
          this.contractorCommissionsToSave = []
          this.manuallyAddedTypeRows = []
          this.searched = true
          return this.loadLegacyOverride()
        })
      }
    },
    loadLegacyOverride () {
      this.legacyOverride = null
      if (!this.monthAssignmentId || !this.monthYear || !this.monthMonth) return

      const monthBegin = new Date(this.monthYear, this.monthMonth - 1, 1)
      const monthEnd = new Date(this.monthYear, this.monthMonth, 0)

      // Fetched by assignment only, not by calendar-month bounds: override periods are payroll
      // periods (e.g. biweekly) and almost never align to a calendar month, so an exact-match
      // date filter here would silently miss real overrides. Matched client-side by overlap
      // against every override on record for this assignment instead.
      return this.getConsultantsEditedTotalPaySilently({
        assignmentId: this.monthAssignmentId
      }).then((overrides) => {
        this.legacyOverride = (overrides || []).find((override) =>
          new Date(override.dateBegin) <= monthEnd && new Date(override.dateEnd) >= monthBegin
        ) || null
      })
    },
    handleClearClick () {
      this.monthBusinessEntityId = null
      this.monthClientId = null
      this.monthUserId = null
      this.monthAssignmentId = null
      this.monthYear = null
      this.monthMonth = null
      this.clientAssignments = []
      this.filteredAssignments = []
      this.assignmentData = null
      this.timeSheetEntriesToSave = []
      this.contractorCommissionsToSave = []
      this.manuallyAddedTypeRows = []
      this.legacyOverride = null
      this.searched = false
      this.$refs.filterForm.resetValidation()
      this.syncFiltersToQuery()
    },
    syncFiltersToQuery () {
      const nextQuery = { ...this.$route.query }
      setOrDeleteQueryParam(nextQuery, 'businessEntityId', this.monthBusinessEntityId)
      setOrDeleteQueryParam(nextQuery, 'client', this.monthClientId)
      setOrDeleteQueryParam(nextQuery, 'consultant', this.monthUserId)
      setOrDeleteQueryParam(nextQuery, 'assignment', this.monthAssignmentId)
      const month = this.monthYear && this.monthMonth
        ? `${this.monthYear}-${String(this.monthMonth).padStart(2, '0')}`
        : null
      setOrDeleteQueryParam(nextQuery, 'month', month)

      if (hasQueryChanged(this.$route.query, nextQuery, ['businessEntityId', 'client', 'consultant', 'assignment', 'month'])) {
        this.$router.replace({ query: nextQuery })
      }
    },
    formatCellDate (dateStr) {
      if (!dateStr) return ''
      const d = new Date(dateStr)
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
      return `${months[d.getMonth()]} ${d.getDate()}`
    },
    availableTypesForWeek (week) {
      const used = new Set(week.typeRows.map(row => row.timeTypeId))
      return this.offerableTypesList.filter(type => !used.has(type.timeTypeId))
    },
    addTypeRowToWeek (week, timeTypeId) {
      this.manuallyAddedTypeRows.push({ weekKey: week.weekKey, timeTypeId })
    },
    handleHoursChange (day, typeRow, newHours) {
      const dayData = this.assignmentData[day.label]
      const existingEntry = dayData.entries.find(e => e.timeTypeId === typeRow.timeTypeId)
      const prev = existingEntry && existingEntry.hours != null ? parseFloat(existingEntry.hours) : null
      const next = newHours != null && newHours !== '' ? parseFloat(newHours) : null
      if (prev === next) return

      if (existingEntry) {
        existingEntry.hours = next
      } else {
        dayData.entries.push({ hours: next, timeSheetEntryId: null, timeTypeId: typeRow.timeTypeId })
      }

      const saveKey = `${day.label}_${typeRow.timeTypeId}`
      const existingToSave = this.timeSheetEntriesToSave.find(e => e.saveKey === saveKey)
      if (existingToSave) {
        existingToSave.hours = next
      } else {
        this.timeSheetEntriesToSave.push({
          saveKey,
          id: existingEntry ? existingEntry.timeSheetEntryId : null,
          hours: next,
          date: day.date,
          dateLabel: day.label,
          assignmentId: this.assignmentData.assignmentId,
          userId: this.assignmentData.consultantId,
          staffId: null,
          timeTypeId: typeRow.timeTypeId
        })
      }
    },
    contractorCommissionChangeHandler (week, newValue) {
      const originalValue = week.contractorCommission

      // Update source so weeksData recomputes and the UI reflects the change
      const commissions = this.assignmentData.contractorCommissionsPerWeek || []
      const sourceCommission = findCommissionForWeek(commissions, week.weekStart)
      if (sourceCommission) {
        sourceCommission.contractorCommission = newValue
      }

      const existing = this.contractorCommissionsToSave.find(e => e.weekKey === week.weekKey)
      if (existing) {
        existing.commission = newValue
        existing.updatedAt = new Date()
        return
      }

      if (originalValue === newValue) {
        return
      }

      this.contractorCommissionsToSave.push({
        id: week.contractorCommissionId,
        year: 0,
        week: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        createdBy: 0,
        updatedBy: 0,
        assignmentId: this.assignmentData.assignmentId,
        commission: newValue,
        weekKey: week.weekKey,
        dateBegin: this.toLocalISODate(week.weekStart),
        dateEnd: this.toLocalISODate(week.weekEnd)
      })
    },
    async saveChanges () {
      try {
        if (this.timeSheetEntriesToSave.length > 0) {
          await this.saveTimeSheetEntries(this.timeSheetEntriesToSave.map(({ saveKey, ...entry }) => entry))
        }
        // Each commission is saved individually because the endpoint requires
        // staffLynxDateBegin and staffLynxDateEnd to identify the week it belongs to,
        // making a single batched call is not feasible with the current API contract.
        for (const commission of this.contractorCommissionsToSave) {
          await this.saveContractorCommissions({
            contractorCommissionsPerWeek: [commission],
            staffLynxDateBegin: commission.dateBegin,
            staffLynxDateEnd: commission.dateEnd
          })
        }
        await this.handleSearchClick()
      } catch (error) {
        // Swallow. The error is handled in the store action with a notification.
      }
    },
    discardChanges () {
      this.handleSearchClick()
    }
  }
}
</script>

<style>
  .monthly-typed-table {
    border: 1px solid rgba(0, 0, 0, 0.12);
  }

  .monthly-ts-header {
    min-width: 100px;
    background-color: #f5f5f5 !important;
    font-weight: 600 !important;
    text-align: center !important;
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  .monthly-ts-type {
    min-width: 90px;
    background-color: #fafafa !important;
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  .monthly-ts-cell {
    min-width: 100px;
    padding: 6px 4px !important;
    vertical-align: top;
    border-right: 1px solid rgba(0, 0, 0, 0.08) !important;
  }

  .monthly-ts-total {
    min-width: 80px;
    vertical-align: middle;
    white-space: nowrap;
    font-weight: 500;
  }
</style>
